import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {PDFApi} from "components/src/api/portal/generated";
import {CopyToClipboard} from "components/src/common/CopyToClipboardButton";
import {DialogCloseCause} from "components/src/common/Enums";
import {FloatingLabel} from "components/src/common/FloatingLabel";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {Dispatch, SetStateAction, useEffect, useId, useState} from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import {useNavigate} from "react-router-dom";

/**
 * The owner should handle the OnClose event. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause: DialogCloseCause) => void;

interface PDFAuthenticatorSetupWizardProps {
    onClose: OnClose
}

/*
 * REST api for accessing the back-end
 */
const pdfApi = new PDFApi(ApiConfig.config());

interface WizardPage {
    id: "intro" | "download" | "qrcode" | "validate" | "finish"
    title: string
}

const sanitizeHTML = (html : string) => {
    return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
}

/**
 * Intro wizard page component
 */
const Intro = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-authenticator-setup-wizard.intro"]))} />
    )
}

/**
 * Download wizard page component
 */
const Download = () => {
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-authenticator-setup-wizard.download"]))} />
    )
}

interface QRCodeProps {
    qrCode: string | undefined
    secretKey: string | undefined
}

/**
 * QR-Code wizard page component
 */
const QRCode = ({qrCode, secretKey}: QRCodeProps) =>
{
    const {t} = useTranslationWithOverride();
    const fieldId = useId();

    return (
        <div>
            <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-authenticator-setup-wizard.scan-qr-code"]))} />
            <img src={`data:image/png;base64,${qrCode}`} alt={t('portal.pdf-authenticator-setup-wizard.qr-code-alt')}/>
            <div className="mb-3">
                <Form.Group controlId={`${fieldId}-secret-key`}>
                    <Form.Label className="text-secondary">{t('portal.pdf-authenticator-setup-wizard.secret-key-label')}</Form.Label>
                    <div className="d-flex flex-row">
                        <Form.Control
                            className="text-wrap"
                            as="textarea"
                            rows={2}
                            value={secretKey}
                            disabled={true}
                            readOnly={true}/>
                        <CopyToClipboard value={secretKey}/>
                    </div>
                </Form.Group>
            </div>
        </div>
    )
}

interface ValidateProps {
    qrCode: string
    otpCode: string
    password: string
    setPassword: Dispatch<SetStateAction<string>>
}

/**
 * Validate wizard page component
 */
const Validate = ({qrCode, otpCode, password, setPassword}: ValidateProps) =>
{
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    const fieldId = useId();

    return (
        <div>
            <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-authenticator-setup-wizard.validate"]))} />
            <img src={`data:image/png;base64,${qrCode}`} alt={t('portal.pdf-authenticator-setup-wizard.validate-qr-code-alt')}/>
            <div className="mb-3">
                <Form.Group controlId={`${fieldId}-otp-code`} className="mb-3">
                    <Form.Label className="text-secondary">{t('portal.pdf-authenticator-setup-wizard.otp-code-label')}</Form.Label>
                    <div className="d-flex flex-row">
                        <Form.Control
                            className="text-wrap"
                            as="textarea"
                            rows={2}
                            value={otpCode}
                            disabled={true}
                            readOnly={true}/>
                        <CopyToClipboard value={otpCode}/>
                    </div>
                </Form.Group>
            </div>

            <FloatingLabel label={t('portal.pdf-authenticator-setup-wizard.validate-label')} className="text-secondary">
                <Form.Control type="text"
                              value={password}
                              placeholder={t('portal.pdf-authenticator-setup-wizard.validate-label')}
                              onChange={e => setPassword(e.target.value)}/>
            </FloatingLabel>
        </div>
    )
}

const Finish = () => {
    const {t} = useTranslationWithOverride();

    return (
        <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.pdf-authenticator-setup-wizard.finish"]))} />
    )
}

export const PDFAuthenticatorSetupWizard = ({onClose}: PDFAuthenticatorSetupWizardProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The current wizard index
     */
    const [wizardIndex, setWizardIndex] = useState<number>(0);
    /**
     * The base64 encoded QR-code containing the OTP secret
     */
    const [otpSecretKeyQRCode, setOtpSecretKeyQRCode] = useState<string>("");
    /**
     * The OTP secret key
     */
    const [otpSecretKey, setOtpSecretKey] = useState<string>("");
    /**
     * Generated OTP QR code for the user which will be used to check whether the user correctly configured PDF Authenticator
     */
    const [otpQRCode, setOtpQRCode] = useState<string>("");
    /**
     * Generated OTP JSON code for the user
     */
    const [oTPJSONCode, setOTPJSONCode] = useState<string>("");
    /**
     * Generated password for the user which will be used to check whether the user correctly configured PDF Authenticator
     */
    const [generatedPassword, setGeneratedPassword] = useState<string>("");
    /**
     * User provided password which should match the generated password
     */
    const [password, setPassword] = useState<string>("");
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // Constant values used for generating the validation QR code
    const PasswordID = "CipherMail";
    const PasswordLength = 6;

    /**
     * The Wizard pages
     */
    const [wizardPages] = useState<WizardPage[]>(
        [
            {id: "intro", title: t('portal.pdf-authenticator-setup-wizard.intro-title')},
            {id: "download", title: t('portal.pdf-authenticator-setup-wizard.download-title')},
            {id: "qrcode", title: t('portal.pdf-authenticator-setup-wizard.scan-qr-code-title')},
            {id: "validate", title: t('portal.pdf-authenticator-setup-wizard.validate-qr-code-title')},
            {id: "finish", title: t('portal.pdf-authenticator-setup-wizard.finish-title')},
        ]
    );

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPSecretKeyQRCode()
            .then(response => {
                setOtpSecretKeyQRCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP Secret Key QR Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPSecretKey()
            .then(response => {
                setOtpSecretKey(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP Secret Key", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPQRCode(PasswordID, PasswordLength)
            .then(response => {
                setOtpQRCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP QR Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetOTPJSONCode(PasswordID, PasswordLength)
            .then(response => {
                setOTPJSONCode(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting OTP JSON Code", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() => {
        if (StringUtils.isBlank(oTPJSONCode)) {
            return;
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        pdfApi.pDFControllerGetPdfPassword(oTPJSONCode)
            .then(response => {
                setGeneratedPassword(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting password", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        oTPJSONCode,
        t
    ]);

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const currentPage = () => {
        return wizardPages[wizardIndex];
    }

    const isLastPage = () => {
        return wizardIndex === (wizardPages.length - 1)
    }

    const isFirstPage = () => {
        return wizardIndex === 0
    }

    const nextPage = () => {
        if (isLastPage()) {
            throw new Error("Last page reached")
        }

        setWizardIndex(index => index + 1)
    }

    const previousPage = () => {
        if (isFirstPage()) {
            throw new Error("At first page")
        }

        setWizardIndex(index => index - 1)
    }

    const cancel = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const finish = () => {
         onClose(DialogCloseCause.Succes);
    }

    const isNextDisabled = () => {
        if (isLastPage() || StringUtils.isBlank(otpSecretKeyQRCode) || StringUtils.isBlank(generatedPassword)) {
            return true;
        }

        return currentPage().id === "validate" && StringUtils.isBlank(password);
    }

    const onClickNext = () => {
        if (currentPage().id === "validate" && generatedPassword !== password) {
            setErrorMessage(t('portal.pdf-authenticator-setup-wizard.password-mismatch'))

            return;
        }

        setErrorMessage(undefined);

        nextPage();
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" scrollable={true}>
            <Modal.Header closeButton>
                <Modal.Title>{currentPage().title}</Modal.Title>
            </Modal.Header>
            <div className="row flex-grow-1">
                <main className="col d-flex flex-column overflow-auto vh-80">
                    <Modal.Body>
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"} scrollIntoView={true}/>
                        {currentPage().id === "intro" && <Intro/>}
                        {currentPage().id === "download" && <Download/>}
                        {currentPage().id === "qrcode" && <QRCode qrCode={otpSecretKeyQRCode} secretKey={otpSecretKey}/>}
                        {currentPage().id === "validate" &&
                            <Validate qrCode={otpQRCode} otpCode={oTPJSONCode} password={password} setPassword={setPassword}/>}
                        {currentPage().id === "finish" && <Finish/>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" disabled={isFirstPage()} onClick={previousPage}>
                            {t('wizard.previous-button')}
                        </Button>
                        <Button variant="primary" disabled={isNextDisabled()} onClick={onClickNext}>
                            {t('wizard.next-button')}
                        </Button>
                        <Button variant="danger" onClick={cancel}>
                            {t('wizard.cancel-button')}
                        </Button>
                        <Button variant="success" disabled={!isLastPage() } onClick={finish}>
                            {t('wizard.finish-button')}
                        </Button>
                    </Modal.Footer>
                </main>
            </div>
        </Modal>
    )
}
